/* eslint-disable @angular-eslint/no-output-rename */
/* eslint-disable @angular-eslint/no-output-native */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    Optional,
    Output,
    Self,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { DsiCurrencyPipe } from '../dsi-currency/dsi-currency.pipe';

const selector = 'ols-currency-input';

@Component({
    selector: selector,
    templateUrl: './currency-input.component.html',
    styleUrls: ['./currency-input.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: CurrencyInputComponent },
    ],
})
export class CurrencyInputComponent
    implements
        MatFormFieldControl<Number>,
        ControlValueAccessor,
        DoCheck,
        OnDestroy {
    static nextID = 0;

    @Input() selectAllOnFocus: boolean;
    @Input() readonly: boolean;

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder: string;

    @Input()
    get value(): number {
        return parseFloat(this._value);
    }
    set value(value: number) {
        this._value = value?.toFixed(2);
        this.stateChanges.next();
    }
    private _value: string;

    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _disabled = false;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-describedby') userAriaDescribedBy: string;

    @Output('blur') blurEvent = new EventEmitter<FocusEvent>();
    @Output('focus') focusEvent = new EventEmitter<FocusEvent>();
    @Output('input') inputEvent = new EventEmitter<Event>();
    @Output('change') changeEvent = new EventEmitter<Event>();
    @Output() valueChange = new EventEmitter<number>();

    @ViewChild('inputElement', { static: true })
    inputElement: ElementRef<HTMLInputElement>;

    stateChanges = new Subject<void>();
    @HostBinding() id = `${selector}-${CurrencyInputComponent.nextID++}`;
    focused = false;
    controlType = selector;
    errorState: boolean;

    get empty(): boolean {
        return !this._value?.length;
    }

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    constructor(
        @Self() @Optional() public ngControl: NgControl,
        private dsiCurrencyPipe: DsiCurrencyPipe
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngDoCheck() {
        if (this.ngControl) {
            this.errorState = this.ngControl.touched && this.ngControl.invalid;
            this.stateChanges.next();
        }
    }

    onFocus(event: FocusEvent) {
        this.focused = true;
        this.focusEvent.emit(event);
        this.stateChanges.next();

        if (this.selectAllOnFocus) {
            setTimeout(() => {
                this.inputElement.nativeElement.select();
            });
        }
    }

    onBlur(event: FocusEvent) {
        this.focused = false;
        this.onNgControlTouched();
        this.blurEvent.emit(event);
        this.stateChanges.next();
    }

    onInput(event: Event) {
        this.value = parseFloat(event.target['value']);
        this.inputEvent.emit(event);
    }

    onChange(event: Event) {
        this.value = parseFloat(event.target['value']);
        this.onNgControlChange(this.value);
        this.valueChange.emit(this.value);
        this.changeEvent.emit(event);
    }

    focus() {
        this.inputElement.nativeElement.focus();
    }

    getFormattedValue(): string {
        if (this.focused) {
            return this.inputElement.nativeElement.value?.replace(
                /[^0-9.-]+/g,
                ''
            );
        }
        return this.dsiCurrencyPipe.transform(this.value);
    }

    writeValue(value: number): void {
        //implements ControlValueAccessor.writeValue
        //Called internally by Angular when the ngControl value changes - don't call directly
        //If there is no ngControl, this is not hit
        this.value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        //implements ControlValueAccessor.setDisabledState
        //Called internally by Angular when the ngControl becomes enabled/disabled - don't call directly
        //If there is no ngControl, this is not hit
        this.disabled = isDisabled;
    }

    private onNgControlChange = (_: number) => {};
    registerOnChange(fn: (newValue: number) => void): void {
        //implements ControlValueAccessor.registerOnChange
        //If there is no ngControl, this is not hit
        this.onNgControlChange = fn;
    }

    private onNgControlTouched = () => {};
    registerOnTouched(fn: () => void): void {
        //implements ControlValueAccessor.registerOnTouched
        //If there is no ngControl, this is not hit
        this.onNgControlTouched = fn;
    }

    setDescribedByIds(ids: string[]): void {
        //implements MatFormFieldControl.setDescribedByIds
        //If this component is not in a MatFormField, this isn't hit
        this.inputElement.nativeElement.setAttribute(
            'aria-describedby',
            ids.join(' ')
        );
    }

    onContainerClick(event: MouseEvent): void {
        //implements MatFormFieldControl.onContainerClick
        //If this component is not in a MatFormField, this isn't hit
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.focus();
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }
}
